import {
  DtoDynamicTestSet,
  DtoEntry,
  DtoTestSet,
  GenericObject,
} from "@/types";
import round from "lodash-es/round";
import { ModuleEnum } from "../../../constants";
import { ReflectionType } from "../../../types/domain-types";
import { SelectedFilters } from "./hooks";
import { filterEntriesByAttributes } from "./hooks/useHydratedCubeData/utils";
import { apiFetch } from "../../../utils/api-fetch";
import { ParticipationQuarterArea } from "./hooks/useCategoryPageData/types";
import { TFunction } from "i18next";

export const renderNumber = (rawNumber: number, showPlusSign: boolean) => {
  const number = round(rawNumber, 0);

  return showPlusSign && number > 0 ? `+${number}` : number;
};

export type BarDataItem = {
  value: number;
  label: string;
};

export function filterEntriesByModule(
  allEntries: DtoEntry[],
  module: ModuleEnum,
  selectedFilters?: SelectedFilters
): DtoEntry[] {
  let moduleEntries = allEntries.filter(entry => entry.module === module);

  if (selectedFilters) {
    const filteredSessionEntries = filterEntriesByAttributes(
      allEntries,
      selectedFilters
    );

    if (filteredSessionEntries.length === 0) {
      return [];
    }

    const allowedSessionIds = new Set(
      filteredSessionEntries.map(entry => entry.sessionId)
    );

    moduleEntries = moduleEntries.filter(entry =>
      allowedSessionIds.has(entry.sessionId)
    );
  }

  return moduleEntries;
}

export function filterTestSetsByModule(
  allTestSets: DtoTestSet[],
  module: ModuleEnum,
  testId?: string
): DtoTestSet[] {
  let moduleTestSets = allTestSets.filter(testSet => testSet.module === module);

  if (testId) {
    moduleTestSets = moduleTestSets.filter(
      testSet => testSet.testId === testId
    );
  }

  return moduleTestSets;
}

export interface FilterTestSetsParams {
  category?: string;
  module?: string;
  testId?: string;
  cubeSide?: string;
}

export function filterTestSets(
  testSets: DtoTestSet[] | undefined = [],
  filters?: FilterTestSetsParams
): DtoTestSet[] {
  if (!filters) {
    return testSets;
  }

  let filteredTestSets = testSets;

  if (filters.category) {
    filteredTestSets = filteredTestSets.filter(
      testSet => testSet.category === filters.category
    );
  }

  if (filters.module) {
    filteredTestSets = filteredTestSets.filter(
      testSet => testSet.module === filters.module
    );
  }

  if (filters.testId) {
    filteredTestSets = filteredTestSets.filter(
      testSet => testSet.testId === filters.testId
    );
  }

  if (filters.cubeSide) {
    filteredTestSets = filteredTestSets.filter(
      testSet => testSet.info.cubeSide === filters.cubeSide
    );
  }

  return filteredTestSets;
}

export function groupTestSetsByCubeSide(
  testSets: DtoTestSet[] | undefined = []
): Record<string, DtoTestSet[]> {
  return testSets.reduce((acc, testSet) => {
    const cubeSide = testSet.info.cubeSide || "undefined";

    if (!acc[cubeSide]) {
      acc[cubeSide] = [];
    }

    acc[cubeSide].push(testSet);

    return acc;
  }, {} as Record<string, DtoTestSet[]>);
}

// ============================================================================
// Common Reflection Utilities
// ============================================================================

export interface DynamicTestSetWithCategory extends DtoDynamicTestSet {
  category: string | undefined;
}

/**
 * Filters dynamic test sets by entries session and module
 */
export function filterDynamicTestSetsByEntriesSession(
  dynamicTestSets: DtoDynamicTestSet[],
  entries: DtoEntry[],
  module: ModuleEnum
): DtoDynamicTestSet[] {
  const allowedSessionIds = new Set(entries.map(entry => entry.sessionId));
  return dynamicTestSets.filter(
    dts => dts.module === module && allowedSessionIds.has(dts.sessionId)
  );
}

/**
 * Enriches dynamic test sets with their corresponding category from test sets
 */
export function enrichDynamicTestSetsWithCategory(
  dynamicTestSets: DtoDynamicTestSet[],
  testSets: DtoTestSet[]
): DynamicTestSetWithCategory[] {
  return dynamicTestSets.map(dts => {
    const testSet = testSets.find(ts => ts._id === dts.testSetId);
    return {
      ...dts,
      category: testSet?.category,
    };
  });
}

/**
 * Converts count to percentage based on total
 */
export function calculatePercentage(count: number, total: number): number {
  if (total === 0) return 0;
  return (count / total) * 100;
}

/**
 * Counts dynamic test sets by reflection type
 */
export function countByReflectionType(
  dynamicTestSets: DtoDynamicTestSet[],
  type: ReflectionType
): number {
  return dynamicTestSets.filter(dts => dts.type === type).length;
}

/**
 * Calculates reflection type counts for a set of dynamic test sets
 */
export function calculateReflectionTypeCounts(
  dynamicTestSets: DtoDynamicTestSet[]
): {
  challengeCount: number;
  leverageCount: number;
} {
  const challengeCount = countByReflectionType(
    dynamicTestSets,
    ReflectionType.Challenge
  );
  const leverageCount = countByReflectionType(
    dynamicTestSets,
    ReflectionType.Leverage
  );

  return { challengeCount, leverageCount };
}

export function calculatePercentagesForNumbers(
  values: Record<string, number | undefined>
): Record<string, number> {
  const entries = Object.entries(values);

  if (entries.length === 0) return {};

  const numericValues = entries.map(([_, value]) => value ?? 0);

  const min = Math.min(...numericValues);
  const max = Math.max(...numericValues);
  const range = max - min;

  if (range === 0) {
    return Object.fromEntries(entries.map(([key, _]) => [key, 1]));
  }

  return Object.fromEntries(
    entries.map(([key, value]) => [key, ((value ?? 0) - min) / range])
  );
}

// Simple in-memory cache for getAnswerSuggestion
const answerSuggestionCache = new Map<string, any>();

async function getAnswerSuggestionWithCache(
  bodyData: GenericObject,
  endpoint: string,
  cacheKeySuffix: string
) {
  const cacheKeyJson = JSON.stringify(bodyData);
  const cacheKey = `${cacheKeyJson}-${cacheKeySuffix}`;

  if (answerSuggestionCache.has(cacheKey)) {
    return answerSuggestionCache.get(cacheKey);
  }

  const data = await apiFetch({
    endpoint,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(bodyData),
  });

  answerSuggestionCache.set(cacheKey, data);
  return data;
}

export async function getAnswerSuggestionForReflectionModule(
  bodyData: GenericObject
) {
  return getAnswerSuggestionWithCache(
    bodyData,
    "/ai-service/summarize-answers-for-reflection-module",
    "reflection"
  );
}

export async function getAnswerSuggestionForSolutionModule(
  bodyData: GenericObject
) {
  return getAnswerSuggestionWithCache(
    bodyData,
    "/ai-service/summarize-answers-for-solution-module",
    "solution"
  );
}

export function getBarChartLabels(t: TFunction): {
  key: ParticipationQuarterArea;
  label: string;
}[] {
  return [
    {
      key: "strong-rejection",
      label: t(
        "frontend.futurebilityCockpit.barChart.labels.strongRejection",
        "Strong Rejection"
      ),
    },
    {
      key: "weak-rejection",
      label: t(
        "frontend.futurebilityCockpit.barChart.labels.weakRejection",
        "Weak Rejection"
      ),
    },
    {
      key: "weak-consent",
      label: t(
        "frontend.futurebilityCockpit.barChart.labels.weakConsent",
        "Weak Consent"
      ),
    },
    {
      key: "strong-consent",
      label: t(
        "frontend.futurebilityCockpit.barChart.labels.strongConsent",
        "Strong Consent"
      ),
    },
  ];
}

export function getBarChartData(data: Record<string, number>, t: TFunction) {
  return getBarChartLabels(t).map(({ key, label }) => ({
    value: data[key] * 100,
    label,
  }));
}

export function translateErrorCode(errorCode: string, t: TFunction): string {
  switch (errorCode) {
    case "INSUFFICIENT_RESPONSES":
      return t(`frontend.futurebilityCockpit.notEnoughData`, "Not enough data");
    case "UNCLEAR":
      return t(`frontend.futurebilityCockpit.notClear`, "Input is not clear");
    default:
      return t(`frontend.futurebilityCockpit.unknownError`, "Unknown error");
  }
}

/**
 * Gets the benchmark value from a single test set by benchmark title.
 * If the title matches "default" or "Default", returns the default benchmark value.
 * Otherwise, searches through the test set's benchmarks for a matching title.
 *
 * @param testSet - The test set to search in
 * @param benchmarkTitle - The title of the benchmark to find
 * @returns The benchmark value or null if not found
 */
export function getBenchmarkValue(
  testSet: DtoTestSet,
  benchmarkTitle: string | undefined
): number | null {
  if (!benchmarkTitle) {
    return testSet.info?.defaultBenchmark ?? null;
  }

  // Search through benchmarks
  for (const benchmark of testSet.benchmarks) {
    // Check if the benchmark label matches
    if (benchmark.label === benchmarkTitle) {
      return null; // Parent benchmarks don't have values
    }

    // Search through benchmark children (a1/b1, a2/b2, etc.)
    if (benchmark.values > 0) {
      for (let n = 1; n <= benchmark.values; n++) {
        const titleKey = `a${n}` as keyof typeof benchmark;
        const valueKey = `b${n}` as keyof typeof benchmark;

        const title = benchmark[titleKey];
        const value = benchmark[valueKey];

        if (String(title) === benchmarkTitle && typeof value === "number") {
          return value;
        }
      }
    }
  }

  return null;
}

export function formatAiHtmlString(htmlString: string): string {
  return htmlString.replaceAll("\n", "<br>").trim();
}
